/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 *
 * Settings
 *
 */

import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import {
  Conditional,
  Space,
  Button,
  Icon,
  Text,
} from '@cashfree-intl/coherent';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import { getActiveNavigation, useQuery } from 'utils/common';
import {
  PG_SETTINGS_OPTIONS,
  NAVIGATION_BAR_OPTIONS,
  NOTIFICATIONS,
  SELF_SERVE_SR_ALERTS,
  SUCCESS_RATE_ALERTS,
  ROUTER_TTL_AUTOREFUND,
  PG_DASHBOARD_SUMMARY_PATH,
} from 'utils/constants';
import { MerchantContext } from 'contexts/MerchantContext';

import BrandingV2 from 'containers/BrandingV2/Loadable';
import SftpConfiguration from 'containers/SftpConfiguration/Loadable';
import TransactionTime from '../TransactionTime';
import makeSelectSettings from './selectors';
import reducer from './reducer';
import saga from './saga';

import CustomIcons from '../../components/CustomIcons';
import EmailNotifications from '../EmailNotifications';
import SettlementBankDetails from '../SettlementBankDetails/index';
import VideoEmbed from '../../components/VideoEmbed';
import Preferences from '../Preferences';
import Wallets from '../Wallets/index';
import WalletDetailsPage from '../WalletDetailsPage';
import BillingConfiguration from '../BillingConfiguration';
import FeatureConfiguration from '../FeatureConfiguration';
import PaymentMethodsV2 from '../PaymentMethodsV2';
import UpfrontTdr from '../UpfrontTdr';
import UpfrontTdrDetails from '../UpfrontTdrDetails';
import SplitSchemeConfiguration from '../SplitSchemeConfiguration';
import { StyledText } from './styled';

import {
  SPLIT_SCHEME_CONFIGURATION,
  PAYMENT_METHODS,
  PAYMENT_METHODS_HELP_LINK,
  HIDE_BACK_BTN,
  REFUND_WALLET_DOC_LINK,
  HIDE_SETTINGS_HEADER,
} from './constants';

import { basename } from '../../store';

export function Settings({ merchantDetails }) {
  useInjectReducer({ key: 'settings', reducer });
  useInjectSaga({ key: 'settings', saga });

  const { isFlowwiseActive, featureToggles = {} } = useContext(MerchantContext);

  const showRouterTTL =
    ROUTER_TTL_AUTOREFUND in featureToggles &&
    featureToggles[ROUTER_TTL_AUTOREFUND];

  const location = useLocation();

  const query = useQuery();
  const env = query.get('env');

  const page = location.pathname.split('/').pop();
  const allNavOptions = NAVIGATION_BAR_OPTIONS[NOTIFICATIONS];

  const pageOption = PG_SETTINGS_OPTIONS.find(
    obj => obj.url === page.toLowerCase(),
  );

  const handleBackPress = () => {
    window.location.href = `${
      process.env.COMMON_BASE_PATH
    }/settings?env=${env || 'prod'}`;
  };

  const getNavigationOptions = () => {
    let navOptions = allNavOptions;
    if (!featureToggles[SELF_SERVE_SR_ALERTS]) {
      navOptions = navOptions.filter(
        option => option.text !== SUCCESS_RATE_ALERTS,
      );
    }

    return navOptions;
  };

  // computing activeNavigation
  let activeNavigation;
  let embedKey;

  const navigationOptions = getNavigationOptions();

  if (pageOption) {
    activeNavigation = pageOption.name;
    if (pageOption.name === NOTIFICATIONS) {
      activeNavigation = getActiveNavigation(location.hash, navigationOptions);
    }
    embedKey = activeNavigation;
  }

  return (
    <div className="pgdashboard-rightsidebar">
      <Conditional
        if={pageOption?.key && !HIDE_SETTINGS_HEADER.includes(pageOption?.key)}
      >
        <div className="settings-title-wrapper w-100">
          <Space alignItems="baseline" gap={1}>
            <div className="title">
              <span className="normal-text">Settings - </span>
              Payment Gateway
            </div>
            <Conditional if={pageOption?.showDocLink}>
              <div>
                <a href={REFUND_WALLET_DOC_LINK} target="_blank">
                  Refund Wallet
                </a>
              </div>
            </Conditional>
          </Space>
          {activeNavigation && <VideoEmbed embedKey={embedKey} />}
        </div>
        <div className="back-navigate-wrapper">
          {pageOption && !HIDE_BACK_BTN.includes(pageOption.key) && (
            <>
              {/* eslint-disable-next-line */}
              <div
                className="back-navigate-left"
                onClick={handleBackPress}
                data-event-name="Button_Back"
              >
                <CustomIcons componentName="CHEVRONDOWN" direction="left" />
                Back
              </div>
              <div className="global-heading">{pageOption.name}</div>
            </>
          )}
        </div>
      </Conditional>
      <Conditional if={pageOption?.key === 'BRANDING'}>
        <Space gap={2} alignItems="start">
          <Space
            gap={2}
            alignItems="center"
            onClick={handleBackPress}
            data-event-name="Button_Back"
          >
            <Icon name="chevron-left" />
            <StyledText color="primary" variant="b14" strong>
              Back
            </StyledText>
          </Space>
          <Space direction="column" gap={0.5}>
            <Text variant="h16">Customise Checkout Page</Text>
            <Text variant="b14" color="bodyLight">
              Customise the checkout page to align with your company&apos;s
              branding. These changes will be reflected across Cashfree
              Checkout, Payment Links, Payment Forms, Subscriptions, and POS,
              ensuring a consistent brand experience
            </Text>
          </Space>
        </Space>
      </Conditional>
      <Conditional if={pageOption?.key === PAYMENT_METHODS}>
        <Space
          alignItems="center"
          justifyContent="space-between"
          className="mb-6"
        >
          <div
            className="back-navigate-left"
            onClick={handleBackPress}
            data-event-name="Button_Back"
          >
            <CustomIcons componentName="CHEVRONDOWN" direction="left" />
            Back
          </div>
          <Button
            link
            size="small"
            onClick={() => window.open(PAYMENT_METHODS_HELP_LINK, '_blank')}
            className="p-0"
            data-event-name="Link_HowToActivatePaymentModes"
          >
            How to activate payment modes?
          </Button>
        </Space>
      </Conditional>
      <Outlet />
      <Routes>
        <Route
          index
          element={
            <Navigate
              to={`${basename}pg/settings/payment-methods${
                env ? `?env=${env}` : ''
              }`}
            />
          }
        />
        <Route
          path="branding"
          element={<BrandingV2 merchantDetails={merchantDetails} />}
        />
        {/* <Route path="payment-methods-v2" element={<PaymentMethodsV2 />} /> */}
        <Route
          path="notifications"
          element={<EmailNotifications activeNavigation={activeNavigation} />}
        />
        <Route path="payment-methods" element={<PaymentMethodsV2 />} />
        <Route path="bank-accounts" element={<SettlementBankDetails />} />
        <Route path="preferences" element={<Preferences />} />
        <Route path="sftp" element={<SftpConfiguration />} />
        <Route path="wallets" element={<Wallets />} />
        {isFlowwiseActive && (
          <Route path="upfront-tdr" element={<UpfrontTdr />} />
        )}
        {isFlowwiseActive && (
          <Route path="upfront-tdr/details" element={<UpfrontTdrDetails />} />
        )}
        {showRouterTTL && (
          <Route path="transaction-time" element={<TransactionTime />} />
        )}
        <Route path="wallets/wallet-details" element={<WalletDetailsPage />} />
        <Route
          path="billing-configuration"
          element={<BillingConfiguration />}
        />
        <Route
          path="feature-configuration"
          element={<FeatureConfiguration />}
        />
        <Route
          path={SPLIT_SCHEME_CONFIGURATION}
          element={<SplitSchemeConfiguration />}
        />
        <Route
          path="*"
          element={
            <Navigate
              to={`${basename}${PG_DASHBOARD_SUMMARY_PATH}${
                env ? `?env=${env}` : ''
              }`}
            />
          }
        />
      </Routes>
    </div>
  );
}

Settings.propTypes = {
  merchantDetails: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  settings: makeSelectSettings(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Settings);
